import React, { useEffect, useState } from 'react';
import moment from 'moment';
import 'moment/locale/ja';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { Button, Col, Row } from 'react-bootstrap';
import { DayPickerRangeController } from 'react-dates/esm';
import { START_DATE } from 'react-dates/constants';
import OutsideClickHandler from 'react-outside-click-handler';
import classNames from 'classnames';
import { bool, func, object, string } from 'prop-types';
import { CALENDAR_DAY_FORMAT } from 'domain/consts';
import {
  ONCE_ONLY_TARGET_PERIOD_PRESETS,
  clampOnceOnlyPeriodRange,
  resolveOnceOnlyPresetRange,
} from 'domain/data-export/consts';
import 'views/molecules/calendar/simpleDateRangePicker.scss';
import './onceOnlyTargetPeriodPicker.scss';

function CalendarMonth({ month }) {
  return (
    <div className="once-only-target-period-picker__month-caption">
      <span>
        {month.format('YYYY')}/{month.format('MM')}
      </span>
    </div>
  );
}

CalendarMonth.propTypes = {
  month: object.isRequired,
};

function SidePanelItem({ text, selected, onClick }) {
  return (
    <div
      role="button"
      tabIndex={0}
      className={classNames('side-panel-item', { selected })}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
    >
      {text}
    </div>
  );
}

SidePanelItem.propTypes = {
  text: string.isRequired,
  selected: bool,
  onClick: func,
};

SidePanelItem.defaultProps = {
  selected: false,
  onClick: () => {},
};

/**
 * Data Export 1回のみ 対象期間 picker.
 * Config-driven presets from consts — does not change global EbisDateRangePicker.
 * Allows ranges longer than 3 months; save-time validation rejects them.
 */
function OnceOnlyTargetPeriodPicker({
  startDate,
  endDate,
  minDate,
  maxDate,
  disabled,
  error,
  onChange,
}) {
  moment.locale('ja');

  const [showModal, setShowModal] = useState(false);
  const [focus, setFocus] = useState(START_DATE);
  const [renderKey, setRenderKey] = useState(Date.now());
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [initialMonth, setInitialMonth] = useState(
    () => (startDate && startDate.clone()) || moment()
  );

  const [appliedState, setAppliedState] = useState({
    startDate: startDate ? startDate.clone().startOf('day') : null,
    endDate: endDate ? endDate.clone().startOf('day') : null,
  });

  const [tempState, setTempState] = useState(appliedState);
  const [customInput, setCustomInput] = useState({
    startDate: appliedState.startDate?.format(CALENDAR_DAY_FORMAT) || '',
    endDate: appliedState.endDate?.format(CALENDAR_DAY_FORMAT) || '',
  });

  const syncFromProps = (nextStart, nextEnd) => {
    const next = {
      startDate: nextStart ? nextStart.clone().startOf('day') : null,
      endDate: nextEnd ? nextEnd.clone().startOf('day') : null,
    };
    setAppliedState(next);
    setTempState(next);
    setCustomInput({
      startDate: next.startDate?.format(CALENDAR_DAY_FORMAT) || '',
      endDate: next.endDate?.format(CALENDAR_DAY_FORMAT) || '',
    });
  };

  useEffect(() => {
    syncFromProps(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const formatInputValue = () => {
    if (!appliedState.startDate || !appliedState.endDate) return '';
    return `${appliedState.startDate.format(CALENDAR_DAY_FORMAT)} ～ ${appliedState.endDate.format(
      CALENDAR_DAY_FORMAT
    )}`;
  };

  const convertToValidatedDate = (rawStart, rawEnd) =>
    clampOnceOnlyPeriodRange(rawStart, rawEnd, { minDate, maxDate });

  const changeDate = ({ nextStart, nextEnd, presetKey = null }) => {
    const { start, end } = convertToValidatedDate(nextStart, nextEnd);
    setTempState({ startDate: start, endDate: end });
    setCustomInput({
      startDate: start?.format(CALENDAR_DAY_FORMAT) || '',
      endDate: end?.format(CALENDAR_DAY_FORMAT) || '',
    });
    setSelectedPreset(presetKey);
    if (start) {
      setInitialMonth(start.clone());
      setRenderKey(Date.now());
    }
  };

  const handlePresetSelect = (presetKey) => {
    const range = resolveOnceOnlyPresetRange(presetKey);
    if (!range) return;
    changeDate({
      nextStart: moment(range.start, CALENDAR_DAY_FORMAT),
      nextEnd: moment(range.end, CALENDAR_DAY_FORMAT),
      presetKey,
    });
  };

  const onDatesChange = ({ startDate: nextStart, endDate: nextEnd }) => {
    let newStart = nextStart;
    let newEnd = nextEnd;
    if (!newEnd) newEnd = newStart;
    else if (!newStart) newStart = newEnd;
    else if (
      tempState.startDate &&
      tempState.endDate &&
      newStart.isSame(tempState.startDate, 'day') &&
      newEnd.isSame(tempState.endDate, 'day') &&
      !newStart.isSame(newEnd, 'day')
    ) {
      newEnd = newStart;
    }
    changeDate({ nextStart: newStart, nextEnd: newEnd, presetKey: null });
  };

  const onInputDateChange = (value, isStart) => {
    const date = moment(value, CALENDAR_DAY_FORMAT, true);
    if (!date.isValid()) {
      setCustomInput((prev) => ({
        ...prev,
        [isStart ? 'startDate' : 'endDate']: value,
      }));
      return;
    }
    if (isStart) {
      changeDate({
        nextStart: date,
        nextEnd: tempState.endDate,
        presetKey: null,
      });
    } else {
      changeDate({
        nextStart: tempState.startDate,
        nextEnd: date,
        presetKey: null,
      });
    }
  };

  const resetTempToApplied = () => {
    setTempState(appliedState);
    setCustomInput({
      startDate: appliedState.startDate?.format(CALENDAR_DAY_FORMAT) || '',
      endDate: appliedState.endDate?.format(CALENDAR_DAY_FORMAT) || '',
    });
    setSelectedPreset(null);
    if (appliedState.startDate) {
      setInitialMonth(appliedState.startDate.clone());
    }
  };

  const onOkClick = () => {
    const { start, end } = convertToValidatedDate(
      tempState.startDate,
      tempState.endDate
    );
    setAppliedState({ startDate: start, endDate: end });
    setShowModal(false);
    onChange({ start, end });
  };

  const onCancelClick = () => {
    resetTempToApplied();
    setShowModal(false);
  };

  const PrevIcon = () => (
    <span className="icon-hover">
      <i className="fas fa-chevron-left" aria-hidden="true" />
    </span>
  );

  const NextIcon = () => (
    <span className="icon-hover">
      <i className="fas fa-chevron-right" aria-hidden="true" />
    </span>
  );

  const toggleCalendar = (e) => {
    if (disabled) return;
    if (!showModal) {
      resetTempToApplied();
      setShowModal(true);
      setRenderKey(Date.now());
    } else {
      resetTempToApplied();
      setShowModal(false);
    }
    e.stopPropagation();
  };

  const wrappingClassNames = classNames(
    'date-range-picker--simple',
    'once-only-target-period-picker',
    'align-self-center',
    { disabled }
  );

  const inputClassNames = classNames(
    'date-range-picker--simple__input-container',
    {
      'date-range-picker--simple__error': !!error,
      show: showModal,
    }
  );

  return (
    <div className={wrappingClassNames}>
      <OutsideClickHandler
        onOutsideClick={() => {
          if (!showModal) return;
          resetTempToApplied();
          setShowModal(false);
        }}
      >
        <div
          role="button"
          tabIndex={0}
          className={inputClassNames}
          onClick={toggleCalendar}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggleCalendar(e);
          }}
        >
          <input
            type="text"
            value={formatInputValue()}
            className="date-range-picker--simple__input"
            readOnly
            disabled={disabled}
          />
        </div>
        <div
          className={classNames('date-range-picker--simple__modal', {
            'once-only-target-period-picker__modal--open': showModal,
          })}
          style={
            showModal
              ? { visibility: 'visible', overflow: '', height: '' }
              : { visibility: 'hidden', overflow: 'hidden', height: '0px' }
          }
        >
          <div className="side-panel">
            {ONCE_ONLY_TARGET_PERIOD_PRESETS.map(({ key, label }) => (
              <SidePanelItem
                key={key}
                text={label}
                selected={selectedPreset === key}
                onClick={() => handlePresetSelect(key)}
              />
            ))}
          </div>
          <div className="main-panel">
            <DayPickerRangeController
              key={renderKey}
              startDateId="onceOnlyStart"
              endDateId="onceOnlyEnd"
              startDate={tempState.startDate}
              endDate={tempState.endDate}
              numberOfMonths={2}
              onDatesChange={onDatesChange}
              onFocusChange={(focusedInput) =>
                setFocus(focusedInput || START_DATE)
              }
              focusedInput={focus}
              monthFormat="YYYY/MM"
              hideKeyboardShortcutsPanel
              noBorder
              initialVisibleMonth={() => initialMonth}
              navPrev={<PrevIcon />}
              navNext={<NextIcon />}
              firstDayOfWeek={1}
              daySize={26}
              minimumNights={0}
              isOutsideRange={(d) =>
                (minDate && d.isBefore(minDate, 'day')) ||
                (maxDate && d.isAfter(maxDate, 'day'))
              }
              renderMonthElement={(monthProps) => (
                <CalendarMonth month={monthProps.month} />
              )}
              transitionDuration={0}
            />
            <Row className="drp-buttons">
              <Col className="drp-inputed" md={7}>
                <div className="input-date">
                  期間：
                  <input
                    type="text"
                    placeholder="-"
                    value={customInput.startDate}
                    onChange={(e) => onInputDateChange(e.target.value, true)}
                  />
                  <span>～</span>
                  <input
                    type="text"
                    placeholder="-"
                    value={customInput.endDate}
                    onChange={(e) => onInputDateChange(e.target.value, false)}
                  />
                </div>
              </Col>
              <Col className="btn-control" md={5}>
                <Button variant="link" size="sm" onClick={onCancelClick}>
                  キャンセル
                </Button>
                <Button variant="secondary" size="sm" onClick={onOkClick}>
                  適用
                </Button>
              </Col>
            </Row>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
}

OnceOnlyTargetPeriodPicker.propTypes = {
  startDate: object,
  endDate: object,
  minDate: object,
  maxDate: object,
  disabled: bool,
  error: string,
  onChange: func,
};

OnceOnlyTargetPeriodPicker.defaultProps = {
  startDate: null,
  endDate: null,
  minDate: undefined,
  maxDate: undefined,
  disabled: false,
  error: '',
  onChange: () => {},
};

export default React.memo(OnceOnlyTargetPeriodPicker);
